import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Loader2, Phone, PhoneIncoming, PhoneOutgoing, Sparkles, CheckCircle2, AlertTriangle, CalendarClock, History, ListTodo } from "lucide-react";

const TIER_STYLE = {
  hot: { label: "Chaud", color: "#f43f5e", bg: "rgba(244,63,94,0.12)" },
  warm: { label: "Tiède", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  cold: { label: "Froid", color: "#38bdf8", bg: "rgba(56,189,248,0.12)" },
};

export const AircallCallsDialog = ({ open, onClose, api, onImported }) => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [simulate, setSimulate] = useState(true);
  const [createTasks, setCreateTasks] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [creatingProcess, setCreatingProcess] = useState(false);
  const [processMsg, setProcessMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setProcessMsg("");
    (async () => {
      try {
        const [s, h] = await Promise.all([api.get("/aircall/calls-sync/status"), api.get("/aircall/calls-sync/history")]);
        setStatus(s.data);
        setHistory(h.data.syncs || []);
        setSimulate(!s.data.connected);
      } catch (e) { /* silencieux */ }
    })();
  }, [open]);

  const runSync = async () => {
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/aircall/calls-sync/sync", { simulate, create_tasks: createTasks });
      setResult(r.data);
      if (r.data.imported > 0) onImported?.();
      const h = await api.get("/aircall/calls-sync/history");
      setHistory(h.data.syncs || []);
    } catch (e) {
      setResult({ errors: [e.response?.data?.detail || "Erreur lors de l'import"], imported: 0, duplicates: 0, tasks_created: 0, details: [] });
    } finally {
      setRunning(false);
    }
  };

  const createDailyProcess = async () => {
    setCreatingProcess(true);
    setProcessMsg("");
    try {
      await api.post("/ai/processes", {
        name: "Import quotidien des appels Aircall",
        agent_key: "vente",
        schedule_type: "daily",
        schedule_time: "18:00",
        allow_actions: true,
        notify: true,
        instructions:
          "Chaque soir, importe les appels Aircall du jour, génère le résumé IA de chaque conversation, qualifie les prospects (score, chaud/tiède/froid) et crée les tâches de rappel en déclenchant l'action sync_aircall_calls" +
          (simulate ? ' avec {"simulate": true} (mode démonstration tant qu\'Aircall n\'est pas connecté)' : "") +
          ". Termine par un compte-rendu : nombre d'appels analysés, prospects chauds détectés et rappels à faire en priorité demain.",
      });
      setProcessMsg("Process quotidien créé (Agent Force de Vente, 18:00) — visible dans la page Agent IA.");
    } catch (e) {
      setProcessMsg(e.response?.data?.detail || "Erreur lors de la création du process");
    } finally {
      setCreatingProcess(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="aircall-calls-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#00b388]" /> Appels Aircall — résumés & qualification IA
          </DialogTitle>
        </DialogHeader>

        {/* Statut connexion */}
        <div className="rounded-lg border border-white/10 p-3 text-sm" data-testid="aircall-calls-status">
          {status === null ? (
            <span className="text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</span>
          ) : status.connected ? (
            <span className="text-emerald-400 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Aircall connecté — les 10 derniers appels réels seront importés
            </span>
          ) : (
            <span className="text-amber-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Aircall non configuré (Hub API → Aircall). Le mode simulation permet de tester tout le flux.
            </span>
          )}
          {status?.imported_total > 0 && (
            <p className="text-xs text-slate-500 mt-1">{status.imported_total} appel(s) déjà importé(s) et analysé(s).</p>
          )}
        </div>

        {/* Simulation toggle */}
        <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
          <div>
            <p className="text-sm text-white flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#f7c935]" /> Mode simulation (appels de démonstration)</p>
            <p className="text-xs text-slate-500 mt-0.5">Génère des appels fictifs réalistes pour valider les résumés IA, la qualification et le suivi.</p>
          </div>
          <Switch checked={simulate} onCheckedChange={setSimulate} data-testid="aircall-calls-simulate-toggle" />
        </div>

        {/* Tasks toggle */}
        <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
          <div>
            <p className="text-sm text-white flex items-center gap-1.5"><ListTodo className="w-3.5 h-3.5 text-[#00b388]" /> Créer les tâches de rappel automatiquement</p>
            <p className="text-xs text-slate-500 mt-0.5">L'IA ajoute une tâche « Rappeler … » dans Mes Tâches (+ agenda) quand un suivi est recommandé.</p>
          </div>
          <Switch checked={createTasks} onCheckedChange={setCreateTasks} data-testid="aircall-calls-tasks-toggle" />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button onClick={runSync} disabled={running || (!simulate && status && !status.connected)}
            className="bg-[#00b388] text-white hover:bg-[#009e78] font-bold" data-testid="aircall-calls-run-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Phone className="w-4 h-4 mr-1.5" />}
            {running ? "Import & analyse en cours…" : "Importer les appels maintenant"}
          </Button>
          <Button onClick={createDailyProcess} disabled={creatingProcess} variant="outline"
            className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="aircall-calls-create-process-btn">
            {creatingProcess ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-1.5" />}
            Créer le process quotidien (18:00)
          </Button>
        </div>
        {processMsg && <p className="text-xs text-[#f7c935]" data-testid="aircall-calls-process-msg">{processMsg}</p>}

        {/* Résultats */}
        {result && (
          <div className="rounded-lg border border-white/10 p-3 space-y-2" data-testid="aircall-calls-results">
            <p className="text-sm font-bold text-white">
              {result.imported} appel(s) analysé(s) · {result.tasks_created || 0} tâche(s) de rappel · {result.duplicates} doublon(s) ignoré(s)
            </p>
            {(result.errors || []).map((e, i) => (
              <p key={i} className="text-xs text-rose-400 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> {e}</p>
            ))}
            {(result.details || []).map((d) => {
              const ts = TIER_STYLE[d.tier] || TIER_STYLE.cold;
              const Dir = d.direction === "inbound" ? PhoneIncoming : PhoneOutgoing;
              return (
                <div key={d.prospect_id + (d.task_id || "")} className="py-1.5 border-t border-white/5" data-testid={`aircall-call-row-${d.prospect_id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <Dir className="w-3.5 h-3.5 text-slate-500" />
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ color: ts.color, background: ts.bg }}>
                      {ts.label} · {d.score}
                    </span>
                    <span className="text-white font-medium">{d.name}</span>
                    {d.company && <span className="text-slate-500 text-xs">{d.company}</span>}
                    {d.task_id && <span className="ml-auto text-[10px] text-[#00b388] flex items-center gap-1"><ListTodo className="w-3 h-3" /> Rappel créé</span>}
                  </div>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{d.summary || d.headline}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Historique */}
        {history.length > 0 && (
          <div className="rounded-lg border border-white/10 p-3" data-testid="aircall-calls-history">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
              <History className="w-3.5 h-3.5" /> Dernières synchronisations
            </p>
            {history.slice(0, 5).map((s) => (
              <div key={s.sync_id} className="flex items-center gap-2 text-xs text-slate-400 py-1 border-t border-white/5">
                <span>{new Date(s.created_at).toLocaleString("fr-FR")}</span>
                <span className="text-slate-500">· {s.trigger === "process" ? "Process IA" : "Manuel"}{s.simulate ? " (simulation)" : ""}</span>
                <span className="ml-auto text-white">{s.imported} appel(s)</span>
                <span>· {s.tasks_created || 0} rappel(s)</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AircallCallsDialog;
